import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry with comprehensive configuration
 * This should be called once at app startup
 */
export function initSentry() {
  // Only initialize if DSN is provided
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN?.trim();

  if (!sentryDsn) {
    console.log('Sentry DSN not found, skipping initialization');
    return;
  }

  // Validate DSN format
  if (sentryDsn.includes('\n') || sentryDsn.includes(' ')) {
    console.error('Invalid Sentry DSN format - contains line breaks or spaces');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    release: process.env.REACT_APP_SENTRY_RELEASE,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy-first configuration
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
        // Elements to ignore in replay
        ignore: ['.sentry-ignore', '.no-capture', '[data-sentry-ignore]'],
        // Don't unmask anything - keep everything masked for privacy
        unmask: [],
        // Don't unblock any media - keep everything blocked
        unblock: [],
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Which URLs to track
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/fresh-rebuild\.netlify\.app/,
      /^https:\/\/.*\.glyyd\.com/,
    ],

    // Session Replay sample rates
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Before sending event to Sentry
    beforeSend(event, hint) {
      // Filter sensitive data
      if (event.request) {
        // Remove cookies
        delete event.request.cookies;

        // Sanitize headers
        if (event.request.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['X-CSRF-Token'];
        }

        // Sanitize query strings
        if (event.request.query_string && typeof event.request.query_string === 'string') {
          event.request.query_string = sanitizeQueryString(event.request.query_string);
        }
      }

      // Remove any PII from error messages
      if (event.exception?.values) {
        event.exception.values.forEach((exception) => {
          if (exception.value) {
            exception.value = sanitizeErrorMessage(exception.value);
          }
        });
      }

      // Don't send events from development unless explicitly enabled
      if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_SENTRY_ENABLE_IN_DEV) {
        return null;
      }

      // Filter out specific errors
      const error = hint.originalException as Error | undefined;

      // Ignore ResizeObserver errors (common and not actionable)
      if (error && error.message && error.message.includes('ResizeObserver loop limit exceeded')) {
        return null;
      }

      // Ignore network errors in development
      if (
        process.env.NODE_ENV === 'development' &&
        error &&
        error.message &&
        (error.message.includes('NetworkError') || error.message.includes('Failed to fetch'))
      ) {
        return null;
      }

      return event;
    },

    // Breadcrumb filtering
    beforeBreadcrumb(breadcrumb, hint) {
      // Filter out noisy breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.level === 'warning') {
        return null;
      }

      // Sanitize breadcrumb data
      if (breadcrumb.data) {
        breadcrumb.data = sanitizeBreadcrumbData(breadcrumb.data);
      }

      return breadcrumb;
    },

    // URLs to track errors from
    allowUrls: [
      /https:\/\/fresh-rebuild\.netlify\.app/,
      /https:\/\/.*\.glyyd\.com/,
      /http:\/\/localhost:\d+/,
    ],

    // Errors to ignore
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      // Facebook related
      'fb_xd_fragment',
      // Safari specific
      'Non-Error promise rejection captured',
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Network errors (handle these differently)
      /Failed to fetch/,
      /NetworkError/,
      /Load failed/,
      // Resize observer (noisy and not actionable)
      'ResizeObserver loop limit exceeded',
      // React development warnings
      'Warning: ',
    ],
  });
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: { id: string; email: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      // Don't include name or other PII
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add custom context for rowing-specific features
 */
export function setRowingContext(context: {
  isAthlete?: boolean;
  isCoach?: boolean;
  hasProfile?: boolean;
  schoolsViewed?: number;
}) {
  Sentry.setContext('rowing', context);
}

/**
 * Track custom transactions for key user actions
 */
export function trackTransaction(name: string, op: string, callback: () => Promise<any>) {
  return Sentry.startSpan({ name, op }, () => {
    return callback();
  });
}

/**
 * Add breadcrumb for user actions
 */
export function addActionBreadcrumb(category: string, message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    category,
    message,
    level: 'info',
    data: data ? sanitizeBreadcrumbData(data) : undefined,
  });
}

// Helper functions for sanitization
function sanitizeQueryString(queryString: string): string {
  const params = new URLSearchParams(queryString);
  const sensitiveParams = ['token', 'api_key', 'secret', 'password', 'auth'];

  sensitiveParams.forEach((param) => {
    if (params.has(param)) {
      params.set(param, '[REDACTED]');
    }
  });

  return params.toString();
}

function sanitizeErrorMessage(message: string): string {
  // Remove email addresses
  message = message.replace(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi, '[EMAIL]');

  // Remove potential API keys or tokens (long alphanumeric strings)
  message = message.replace(/[a-zA-Z0-9]{32,}/g, '[REDACTED]');

  // Remove credit card numbers
  message = message.replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '[CARD]');

  return message;
}

function sanitizeBreadcrumbData(data: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {};
  const sensitiveKeys = ['password', 'token', 'secret', 'api_key', 'email', 'ssn', 'credit_card'];

  for (const [key, value] of Object.entries(data)) {
    if (sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'string' && value.length > 100) {
      // Truncate long strings
      sanitized[key] = value.substring(0, 100) + '...';
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}
